import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, LOCALE_ID, signal } from '@angular/core';
import { DatePipe, formatDate } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { CodeIdChipComponent } from '../../components/code-id-chip/code-id-chip.component';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import {
  kategoriePillFuer,
  PipeKategoriePillComponent,
} from '../../components/pipe-kategorie-pill/pipe-kategorie-pill.component';
import { ResPillComponent, ResPillStatus } from '../../components/res-pill/res-pill.component';
import { StatGroupComponent, StatItem } from '../../components/stat-group/stat-group.component';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import { TestRunService } from '../../services/test-run.service';
import { ProjectRoleService } from '../../services/project-role.service';
import { TestRunScheduleService } from '../../services/test-run-schedule.service';
import { TestRunSchedule } from '../../models/test-run-schedule.model';
import {
  PipelineFehlerKategorie,
  StartedTestRunExecution,
  TestRun,
  TestRunExecution,
} from '../../models/test-run.model';
import type { RunTarget } from '../../services/execution-job.service';
import {
  StartRunnerRoundDialogComponent,
  type StartRunnerRoundDialogData,
} from './start-runner-round-dialog/start-runner-round-dialog.component';
import {
  TestRunScheduleDialogComponent,
  type TestRunScheduleDialogData,
  type TestRunScheduleDialogResult,
} from './test-run-schedule-dialog/test-run-schedule-dialog.component';
import { TEST_RUN_TO_RES_PILL } from '../../shared/test-run-outcome';
import { formatDurationMs } from '../../shared/duration.util';
import { ListFocusScrollDirective } from '../../shared/scroll-container/list-focus-scroll.directive';

/**
 * Durchlaeufe eines Testlaufs (NEX-1037) — die Zwischenebene zwischen
 * Testlauf-Uebersicht und Runner. Jede Wiederholung erscheint hier als eigener
 * Durchlauf, damit die Uebersicht bei einer Zeile pro Lauf bleibt.
 *
 * <p>Kopfdaten kommen aus dem bestehenden Lauf-Detail (`GET /test-runs/{id}`);
 * einen eigenen Header-Endpunkt braucht es dafuer nicht.
 */
@Component({
  selector: 'app-testlauf-durchlaeufe',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ListFocusScrollDirective,
    DatePipe,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslatePipe,
    AvatarComponent,
    CodeIdChipComponent,
    PageHeaderComponent,
    PipeKategoriePillComponent,
    ResPillComponent,
    StatGroupComponent,
    ProjectNavComponent,
  ],
  templateUrl: './testlauf-durchlaeufe.component.html',
  styleUrl: './testlauf-durchlaeufe.component.scss',
})
export class TestlaufDurchlaeufeComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly runService = inject(TestRunService);
  private readonly scheduleService = inject(TestRunScheduleService);
  private readonly translate = inject(TranslateService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly locale = inject(LOCALE_ID);

  readonly projectId = this.route.snapshot.paramMap.get('projectId') ?? '';
  readonly runId = this.route.snapshot.paramMap.get('runId') ?? '';

  readonly run = signal<TestRun | null>(null);
  readonly executions = signal<TestRunExecution[]>([]);
  readonly loading = signal(true);
  /** Die Kopfdaten entscheiden, ob vor dem Start ein Rechner gewaehlt wird (NEX-1601). */
  readonly headLoading = signal(true);

  /**
   * Zwei Fehler, zwei Signale: laedt die Liste nicht, gibt es nichts zu zeigen.
   * Fehlen nur die Kopfdaten, bleibt die geladene Liste stehen — sie ist der
   * Inhalt der Seite (NEX-1040 Review, sgraetz W4).
   */
  readonly listError = signal(false);
  readonly headError = signal(false);
  readonly isProjectReader = inject(ProjectRoleService).isProjectReader;
  readonly starting = signal(false);
  /** Nur der Retest-Knopf zeigt den Kreisel; gesperrt sind waehrend `starting` beide Knoepfe. */
  readonly retestStarting = signal(false);

  readonly runKey = computed(() => this.run()?.key ?? '');

  /** Nur ein Rechner-Lauf kennt einen Zeitplan (NEX-1758). `null`: keiner oder nicht geladen. */
  readonly schedule = signal<TestRunSchedule | null>(null);

  /** Den Zeitplan pflegt, wer schreiben darf, und nur an einem Rechner-Lauf. */
  readonly canSchedule = computed(() => this.run()?.source === 'TCDL' && !this.isProjectReader());

  /** Ein Zeitplan ist pausiert, wenn man ihn ausgeschaltet hat oder der Server ihn angehalten hat. */
  readonly schedulePaused = computed(() => {
    const schedule = this.schedule();
    return schedule !== null && (!schedule.enabled || schedule.pausedReason !== null);
  });

  /**
   * „Mo, 07.10., 02:00" in Ortszeit. Die deutsche Kurzform des Wochentags endet auf einen Punkt
   * („Mo."), der vor dem Komma doppelt wirkte.
   */
  readonly nextStart = computed(() => {
    const nextRunAt = this.schedule()?.nextRunAt;
    if (!nextRunAt) return null;
    const weekday = formatDate(nextRunAt, 'EEEEEE', this.locale).replace(/\.$/, '');
    return `${weekday}, ${formatDate(nextRunAt, 'dd.MM., HH:mm', this.locale)}`;
  });

  /**
   * Die Art kommt als Query-Parameter von der Uebersicht mit. Das Lauf-Detail
   * gibt sie nicht heraus: `runKind` steht nur an der Entity, und `runType` ist
   * die Test-Kategorie, die jeder Schreibpfad hart auf `MANUAL` setzt. Faellt der
   * Parameter weg (Lesezeichen, direkter Link), gilt „manuell". Nimmt ein
   * Folgeticket `runKind` ins Detail-DTO auf, faellt der Umweg weg.
   */
  readonly artParam = this.route.snapshot.queryParamMap.get('art');
  readonly automated = computed(() => this.artParam === 'AUTOMATED');

  readonly subtitle = computed(() => {
    const run = this.run();
    if (!run) return '';
    const name = run.name ?? '';
    return run.environment
      ? this.translate.instant('testRunExecutions.subtitleWithEnvironment', {
          name,
          environment: run.environment,
        })
      : name;
  });

  readonly stats = computed<StatItem[]>(() => {
    const all = this.executions();
    return [
      { label: this.translate.instant('testRunExecutions.stats.total'), value: all.length },
      {
        label: this.translate.instant('testRunExecutions.stats.passed'),
        value: all.filter((e) => e.status === 'PASSED').length,
        color: 'var(--to-green-fg)',
      },
      {
        label: this.translate.instant('testRunExecutions.stats.failed'),
        value: all.filter((e) => e.status === 'FAILED').length,
        color: 'var(--to-red-fg)',
      },
    ];
  });

  /**
   * Der Durchlauf, dessen nicht bestandene Testfaelle ein Retest wiederholen wuerde; `null`: kein Knopf.
   * Grundlage ist der neueste, sobald keiner mehr laeuft — die Liste kommt neueste zuerst. Pipeline-Laeufe
   * schliesst der Server aus (400); fehlt der Kopf, laesst sich das nicht pruefen.
   */
  readonly retestBasis = computed<TestRunExecution | null>(() => {
    const run = this.run();
    if (!run || this.isProjectReader()) return null;
    if (run.source === 'PIPELINE' || (this.automated() && run.source !== 'TCDL')) return null;
    const all = this.executions();
    const newest = all[0];
    if (!newest || all.some((e) => e.live)) return null;
    // Einen Retest von `newest` muss niemand ausschliessen: er waere neuer und stuende selbst oben.
    return newest.retestCaseCount ? newest : null;
  });

  constructor() {
    this.load();
  }

  private load(): void {
    this.loading.set(true);
    this.listError.set(false);
    this.headError.set(false);
    this.headLoading.set(true);
    this.runService
      .get(this.projectId, this.runId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (run) => {
          this.run.set(run);
          this.headLoading.set(false);
          if (run.source === 'TCDL') {
            this.loadSchedule();
          }
        },
        error: () => {
          this.headError.set(true);
          this.headLoading.set(false);
        },
      });
    this.runService
      .listExecutions(this.projectId, this.runId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (executions) => {
          this.executions.set(executions);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.listError.set(true);
        },
      });
  }

  /**
   * Die Anzeige ist ein Zusatz: Laedt der Zeitplan nicht, fehlt sie still, statt die Seite mit
   * einem Fehler zu belegen. Ohne Zeitplan antwortet der Server mit 404, der Service macht `null` daraus.
   */
  private loadSchedule(): void {
    this.scheduleService
      .get(this.projectId, this.runId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (schedule) => this.schedule.set(schedule),
        error: () => this.schedule.set(null),
      });
  }

  /** Der Dialog liefert den gespeicherten Stand mit; ein neues GET braucht es danach nicht. */
  openSchedule(): void {
    const run = this.run();
    if (!run) return;
    this.dialog
      .open<TestRunScheduleDialogComponent, TestRunScheduleDialogData, TestRunScheduleDialogResult>(
        TestRunScheduleDialogComponent,
        {
          data: { projectId: this.projectId, runId: this.runId, runKey: run.key, runName: run.name },
          autoFocus: 'dialog',
        },
      )
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result !== undefined) {
          this.schedule.set(result.schedule);
        }
      });
  }

  pillStatus(execution: TestRunExecution): ResPillStatus {
    return TEST_RUN_TO_RES_PILL[execution.status];
  }

  /** Eine laufende Ausfuehrung zeigt immer den Live-Chip, eine nicht aussagekraeftige ihre Kategorie (NEX-1548). */
  kategorie(execution: TestRunExecution): PipelineFehlerKategorie | null {
    return execution.live ? null : kategoriePillFuer(execution);
  }

  pillLabel(execution: TestRunExecution): string {
    return this.translate.instant('testRunExecutions.result.' + execution.status);
  }

  /** „7/7" — bewusst als ein Wert, damit die Spalte im Design eine Zelle bleibt. */
  steps(execution: TestRunExecution): string {
    return `${execution.stepsPassed}/${execution.stepsTotal}`;
  }

  duration(execution: TestRunExecution): string {
    return formatDurationMs(execution.durationMs);
  }

  comments(execution: TestRunExecution): string {
    if (execution.commentCount === 0) return '—';
    const key =
      execution.commentCount === 1 ? 'testRunExecutions.commentCountOne' : 'testRunExecutions.commentCountMany';
    return this.translate.instant(key, { count: execution.commentCount });
  }

  /**
   * Ein abgeschlossener Durchlauf oeffnet seine Ergebnisse, wenn der Lauf manuell ist
   * oder von einem verbundenen Rechner stammt (TCDL). Laufende Durchlaeufe und
   * Pipeline-Laeufe bleiben beim Runner, ebenso ein automatisierter Lauf, dessen Kopf
   * (noch) fehlt. Fehlt `?art`, entscheidet die Ergebnis-Ansicht selbst.
   */
  openExecution(execution: TestRunExecution): void {
    const target = ['/projects', this.projectId, 'test-runs', this.runId, 'executions', execution.id];
    const showResults = !execution.live && (!this.automated() || this.run()?.source === 'TCDL');
    this.router.navigate(showResults ? [...target, 'results'] : target);
  }

  /**
   * Startet den naechsten Durchlauf und folgt dem `nextAction` des Servers. Ein
   * automatisierter Lauf wartet auf die Pipeline, statt den Runner zu oeffnen —
   * dort gibt es nichts zu klicken. Ein Runner-Lauf (Quelle TCDL) fragt vorher nach
   * dem Rechner und zeigt danach die Live-Ansicht des neuen Durchlaufs (NEX-1601).
   */
  startExecution(): void {
    if (this.starting()) return;
    this.starting.set(true);
    this.chooseTarget()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((target) => {
        if (target === undefined) {
          this.starting.set(false);
          return;
        }
        this.start(target);
      });
  }

  /** Wie {@link startExecution}, nur mit `retestOf` — der Server waehlt die zu wiederholenden Testfaelle. */
  startRetest(basis: TestRunExecution): void {
    if (this.starting()) return;
    this.starting.set(true);
    this.retestStarting.set(true);
    this.chooseTarget()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((target) => {
        if (target === undefined) {
          this.starting.set(false);
          this.retestStarting.set(false);
          return;
        }
        this.start(target, basis.id);
      });
  }

  /** `null`: kein Ziel noetig; `undefined`: Auswahl abgebrochen. */
  private chooseTarget(): Observable<RunTarget | null | undefined> {
    const run = this.run();
    if (run?.source !== 'TCDL') return of(null);
    return this.dialog
      .open<StartRunnerRoundDialogComponent, StartRunnerRoundDialogData, RunTarget>(StartRunnerRoundDialogComponent, {
        data: { runKey: run.key, runName: run.name },
        autoFocus: 'dialog',
      })
      .afterClosed();
  }

  private start(target: RunTarget | null, retestOf?: string): void {
    this.startRequest(target, retestOf)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (started) => {
          this.starting.set(false);
          this.retestStarting.set(false);
          if (started.nextAction === 'TRIGGER_PIPELINE') {
            this.snackBar.open(this.translate.instant('testRunExecutions.pipelineQueued'), undefined, {
              duration: 4000,
            });
            this.load();
            return;
          }
          this.router.navigate([
            '/projects',
            this.projectId,
            'test-runs',
            this.runId,
            'executions',
            started.executionId,
          ]);
        },
        error: (error: unknown) => {
          this.starting.set(false);
          this.retestStarting.set(false);
          const refusal = retestOf ? this.retestRefusal(error) : null;
          if (refusal !== null) {
            this.snackBar.open(refusal, undefined, { duration: 4000 });
            this.load();
            return;
          }
          this.snackBar.open(this.startErrorMessage(error, target), undefined, { duration: 4000 });
        },
      });
  }

  /** Ohne Ziel und ohne Retest bleibt der Aufruf so kurz wie vor NEX-1757. */
  private startRequest(target: RunTarget | null, retestOf?: string): Observable<StartedTestRunExecution> {
    if (retestOf) return this.runService.startExecution(this.projectId, this.runId, target ?? undefined, retestOf);
    if (target) return this.runService.startExecution(this.projectId, this.runId, target);
    return this.runService.startExecution(this.projectId, this.runId);
  }

  /**
   * 400 und 409 sagen, dass es fuer diesen Retest keine Grundlage (mehr) gibt — etwa weil inzwischen
   * ein anderer gestartet hat. Der Servertext nennt den Grund, die neu geladene Liste den Stand.
   * Absagen eines Rechners tragen einen `code` und bleiben bei {@link startErrorMessage}.
   */
  private retestRefusal(error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse) || (error.status !== 400 && error.status !== 409)) return null;
    if (error.error?.code) return null;
    const message = error.error?.error;
    return typeof message === 'string' && message
      ? message
      : this.translate.instant('testRunExecutions.retest.startError');
  }

  /** Erkannt wird die Absage am Code, nicht am Statuscode — wie im Ausfuehren-Dialog. */
  private startErrorMessage(error: unknown, target: RunTarget | null): string {
    const code = error instanceof HttpErrorResponse ? error.error?.code : null;
    if (code === 'RUNNER_OFFLINE' && target?.kind === 'runner') {
      return this.translate.instant('testRunExecutions.runnerOffline', { name: target.runnerName });
    }
    if (code === 'NO_MATCHING_RUNNER' && target?.kind === 'labels') {
      return this.translate.instant('testRunExecutions.noMatchingRunner', { labels: target.labels.join(', ') });
    }
    if (code === 'RUNNER_JOB_OPEN') {
      return this.translate.instant('testRunExecutions.jobOpen');
    }
    return this.translate.instant('testRunExecutions.startError');
  }
}
